import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { TimeZone } from '../../../time/timezone.js';
import { DayOfYear } from '../day-of-year.js';
import { TimeZoneRegion } from '../time-zone-region.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isNullOrUndefined } from '../../../utility/null-safety.js';
import { CalendarSnapshot } from './calendar-snapshot.js';
import { IDailyCalendarSnapshot } from '../../../contracts/daily-calendar-snapshot.js';
import { MonthOfYear } from '../month-of-year.js';
import { DailyCalendarSnapshot } from './daily-calendar-snapshot.js';
import { MonthlyCalendarStateSerializer } from '../../../serialization/monthly-calendar-state-serializer.js';
import {
  cloneMap,
  DataModelBuilder,
  isSameData,
  isSameMap,
  SerializationContext,
  SerializedData,
} from '../../dto/dto.js';

export class MonthlyCalendarSnapshot extends CalendarSnapshot {
  static readonly TYPE_ID: string = '30538a42dc5c4377bf5976e5bf91dc94';

  public readonly date: dayjs.Dayjs;

  constructor(
    public readonly monthOfYear: MonthOfYear,
    public readonly timezone: TimeZoneRegion,
    private readonly calendars: Map<number, DailyCalendarSnapshot>,
  ) {
    super();
    this.date = this.monthOfYear.toDate(TimeZone.ofRegion(this.timezone));
  }

  get $typeId(): string {
    return MonthlyCalendarSnapshot.TYPE_ID;
  }

  get timestamp(): number {
    return this.date.valueOf();
  }

  static empty(monthOfYear: MonthOfYear, timezone: TimeZoneRegion): MonthlyCalendarSnapshot {
    return new MonthlyCalendarSnapshot(monthOfYear, timezone, new Map<number, DailyCalendarSnapshot>());
  }

  static fromMap(context: SerializationContext, map: SerializedData): MonthlyCalendarSnapshot {
    const monthOfYear: MonthOfYear = context.fromData(MapUtils.get<SerializedData>(map, 'monthOfYear'));
    const timezone: TimeZoneRegion = toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion);

    const calendars = MonthlyCalendarStateSerializer.deserialize(
        context.fromBinaryBlob(MapUtils.getRaw(map, 'data')),
        (calendars, dayOfMonth) => {
          return new DailyCalendarSnapshot(
              new DayOfYear(monthOfYear.year, monthOfYear.month, dayOfMonth),
              timezone,
              calendars,
          );
        },
    );

    return new MonthlyCalendarSnapshot(monthOfYear, timezone, calendars);
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'monthOfYear': context.toData(this.monthOfYear),
      'date': context.encodeDate(this.date),
      'timezone': fromEnumAsString(this.timezone),
      'data': context.toBinaryBlob(MonthlyCalendarStateSerializer.serialize(this.calendars)),
    };
  }

  clone(): this {
    return new MonthlyCalendarSnapshot(
        this.monthOfYear.clone(),
        this.timezone,
        cloneMap(this.calendars, (cs) => cs.clone()),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameMap(other.calendars, this.calendars, (a, b) => isSameData(a, b)) &&
        isSameData(other.monthOfYear, this.monthOfYear) &&
        other.timezone === this.timezone;
  }

  addDailyCalendar(dailyCalendar: IDailyCalendarSnapshot): boolean {
    const dayOfMonth = dailyCalendar.dayOfYear.day;
    if(this.calendars.has(dailyCalendar.dayOfYear.day)) {
      return false;
    }

    this.calendars.set(dayOfMonth, DailyCalendarSnapshot.fromOther(dailyCalendar));
    return true;
  }

  getDailyCalendar(dayOfMonth: number): IDailyCalendarSnapshot | null {
    return this.calendars.get(dayOfMonth) ?? null;
  }

  getOrCreateDailyCalendar(dayOfMonth: number): IDailyCalendarSnapshot {
    let calendarState = this.calendars.get(dayOfMonth) ?? null;
    if(isNullOrUndefined(calendarState)) {
      calendarState = DailyCalendarSnapshot.empty(
          new DayOfYear(this.monthOfYear.year, this.monthOfYear.month, dayOfMonth),
          this.timezone,
      );

      this.calendars.set(dayOfMonth, calendarState);
    }

    return calendarState;
  }

  toDiagnosticsObject(): SerializedData {
    const calendars: SerializedData = {};
    this.calendars.forEach((v, k) => {
      calendars[k.toString()] = v.toDiagnosticsObject();
    });

    return {
      'monthOfYear': this.monthOfYear.uniqueId,
      'date': TimeZone.encodeDate(this.date),
      'timezone': fromEnumAsString(this.timezone),
      'calendars': calendars,
    };
  }
}

export class MonthlyCalendarSnapshotBuilder extends DataModelBuilder<MonthlyCalendarSnapshot> {
  build(context: SerializationContext, map: SerializedData): MonthlyCalendarSnapshot {
    return MonthlyCalendarSnapshot.fromMap(context, map);
  }
}
